/**
 * AdminContestConfigPage — Cutest Pluck contest active-round config editor.
 *
 * Route: `/admin/contest/config`. Admin-only surface for setting
 * dates (entry_open_at, entry_close_at, voting_close_at), toggling
 * `is_active`, adjusting `prize_amount_cents`, and — added
 * 2026-07-21 in the Feature C+D+E build — configuring `max_entries`
 * (Feature D — cap; blank = unlimited) and `winner_count` (Feature E
 * — number of ranks credited on close, defaults to 10).
 *
 * The header renders the round name + round_number when the backend
 * surfaces them (post-migration). Pre-migration the fallback synth
 * yields a "Round 1 (legacy)" label from the seed-fallback path in
 * `contest_rounds.get_active_round_or_seed`.
 *
 * "Close round + start new" modal — Feature C — triggers when the
 * admin wants to finalize the current round and open the next one
 * with fresh dates/config. POSTs to `/admin/contest/rounds/close`
 * then `/admin/contest/rounds` with `activate=true`.
 *
 * The contest_id is now sourced from `GET /admin/contest/rounds/active`
 * (or the config endpoint's response) — no more hardcoded CONTEST_ID.
 * Pinned by the source-scan drift-guard in
 * `contestConstants.driftGuard.test.js`.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, AlertTriangle, RotateCcw } from "lucide-react";
import AdminContestTabs from "@/components/admin/AdminContestTabs";
import {
  CONTEST_DISPLAY_NAME_FALLBACK,
  INTERNAL_ROUND_ID_PATTERN,
  getContestDisplayName,
} from "@/lib/contestConstants";

// Item 1 (2026-07-24) — mirror of DISPLAY_NAME_MAX_LEN on the backend
// (`contest_entries.py`). Enforced client-side on the `maxLength` input
// attribute + server-side by the PATCH validator.
const DISPLAY_NAME_MAX_LEN = 80;

// Datetime-local <input> wants "YYYY-MM-DDTHH:mm" (no timezone).
// Server stores ISO with tz; adapt both directions.
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(local) {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function AdminContestConfigPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState("");
  const [postSaveWarnings, setPostSaveWarnings] = useState([]);

  // Field-level draft state.
  const [entryOpen, setEntryOpen] = useState("");
  const [entryClose, setEntryClose] = useState("");
  const [votingClose, setVotingClose] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [prizeDollars, setPrizeDollars] = useState("");
  // Feature D + E — new draft fields.
  const [maxEntries, setMaxEntries] = useState("");   // "" = unlimited
  const [winnerCount, setWinnerCount] = useState("10");
  // Item 1 (2026-07-24) — admin-editable public display name. Empty
  // string = "use default fallback". Client-side regex short-circuits
  // an internal-ID-shaped entry with an inline warning + disables Save.
  const [displayName, setDisplayName] = useState("");

  // Feature C — close-round + start-new modal.
  const [closeOpen, setCloseOpen] = useState(false);
  const [nextRoundName, setNextRoundName] = useState("");
  const [closingRound, setClosingRound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Endpoint accepts an optional contest_id query param; omitting
      // it → backend's Query(CONTEST_ID) default → resolves via the
      // active-round lookup.
      const r = await api.get("/admin/contest/config");
      setCfg(r.data);
      setEntryOpen(isoToLocalInput(r.data?.entry_open_at));
      setEntryClose(isoToLocalInput(r.data?.entry_close_at));
      setVotingClose(isoToLocalInput(r.data?.voting_close_at));
      setIsActive(Boolean(r.data?.is_active));
      const cents = Number(r.data?.prize_amount_cents || 0);
      setPrizeDollars((cents / 100).toFixed(2));
      const mx = r.data?.max_entries;
      setMaxEntries(mx == null ? "" : String(mx));
      setWinnerCount(String(r.data?.winner_count ?? 10));
      // Item 1 (2026-07-24) — display_name loads from the same
      // `/admin/contest/config` response. Null / missing → empty
      // input (renders as "use default").
      const dn = r.data?.display_name;
      setDisplayName(typeof dn === "string" ? dn : "");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSave() {
    setInlineError("");
    setPostSaveWarnings([]);
    setSaving(true);
    const cents = Math.round(parseFloat(prizeDollars || "0") * 100);
    // Feature D — empty input string means "unlimited" → send `null`.
    const parsedMax =
      maxEntries.trim() === "" ? null : Math.max(1, parseInt(maxEntries, 10) || 1);
    const parsedWinnerCount = Math.max(
      1,
      Math.min(10, parseInt(winnerCount, 10) || 10),
    );
    // Item 1 (2026-07-24) — display_name: empty-after-trim → null so
    // the server stores null. Server also normalizes trim + null; this
    // is the belt-and-braces client normalize.
    const trimmedDisplayName = displayName.trim();
    const payload = {
      contest_id: cfg?.contest_id,
      is_active: isActive,
      prize_amount_cents: Number.isFinite(cents) ? cents : undefined,
      entry_open_at: localInputToIso(entryOpen),
      entry_close_at: localInputToIso(entryClose),
      voting_close_at: localInputToIso(votingClose),
      max_entries: parsedMax,
      winner_count: parsedWinnerCount,
      display_name: trimmedDisplayName === "" ? null : trimmedDisplayName,
    };
    try {
      const r = await api.patch("/admin/contest/config", payload);
      setCfg(r.data);
      setPostSaveWarnings(r.data?.warnings || []);
      const dn = r.data?.display_name;
      setDisplayName(typeof dn === "string" ? dn : "");
      toast.success("Contest config saved");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const errCode = typeof detail === "object" ? detail?.error : null;
      if (
        errCode === "date_order_invalid" ||
        errCode === "prize_amount_out_of_range" ||
        errCode === "winner_count_out_of_range" ||
        errCode === "max_entries_invalid" ||
        errCode === "display_name_invalid"
      ) {
        setInlineError(
          (typeof detail === "object" && detail?.message) ||
            formatApiErrorDetail(detail),
        );
      } else {
        toast.error(formatApiErrorDetail(detail) || "Save failed.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleCloseRoundAndStartNew() {
    if (!nextRoundName.trim()) return;
    setClosingRound(true);
    try {
      await api.post("/admin/contest/rounds/close");
      await api.post("/admin/contest/rounds", {
        name: nextRoundName.trim(),
        activate: true,
      });
      toast.success("New round started");
      setCloseOpen(false);
      setNextRoundName("");
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't close + start new round.",
      );
    } finally {
      setClosingRound(false);
    }
  }

  if (loading) {
    return (
      <div
        className="flex items-center justify-center rounded-2xl border border-border bg-pp-surface p-10"
        data-testid="admin-contest-config-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin text-brand-orange" />
      </div>
    );
  }

  const creditedCount = Number(cfg?.credited_entry_count || 0);
  const entriesCurrent = Number(cfg?.entries_current || 0);
  const capValue = cfg?.max_entries;

  // Item 1 (2026-07-24) — client-side warning + live preview.
  //   - `displayNameWarning`: true iff the current draft matches the
  //     internal-ID pattern. Disables Save until cleared. Server has
  //     an identical guard as belt #2.
  //   - `previewName`: what the public will see, computed via the
  //     shared accessor so the preview is byte-parity with what the
  //     rules page / share OG actually render. If the draft is
  //     internal-ID-shaped, the accessor short-circuits to the raw
  //     `cfg.name` (if clean) or the hardcoded fallback.
  //
  // Kept as plain const derivations (no `useMemo`) so hook order
  // stays stable across the loading-branch early return above.
  const displayNameTrimmed = displayName.trim();
  const displayNameWarning =
    displayNameTrimmed !== "" &&
    INTERNAL_ROUND_ID_PATTERN.test(displayNameTrimmed);
  const previewName = getContestDisplayName({
    displayName: displayNameTrimmed,
    contestName: cfg?.name,
  });

  return (
    <div className="space-y-6" data-testid="admin-contest-config-page">
      <AdminContestTabs />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1
            className="font-heading text-2xl font-bold text-pp-text"
            data-testid="admin-contest-config-round-header"
          >
            {/* Human override 2026-07-21 Step 2: `name` is STANDALONE
                (no baked-in "Round N"). Compose the round-number
                and status pill at render time from their own fields
                so future rounds get a clean auto-generated label
                like `Cutest Pluck on the Block — 2027 (Round 2) — Active`
                without double-Round-N bugs. */}
            <span data-testid="admin-contest-config-round-name">
              {cfg?.name || `Round ${cfg?.round_number ?? 1}`}
            </span>
            {cfg?.round_number ? (
              <span
                className="text-brand-orange"
                data-testid="admin-contest-config-round-number"
              >
                {" "}(Round {cfg.round_number})
              </span>
            ) : null}
            {cfg?.status ? (
              <span
                className="text-muted-foreground"
                data-testid="admin-contest-config-round-status"
              >
                {" — "}{cfg.status.charAt(0).toUpperCase() + cfg.status.slice(1)}
              </span>
            ) : null}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Set contest dates, active toggle, prize amount, entry cap, and
            winner count for the active round.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="rounded-full bg-pp-surface-2 px-3 py-1 text-xs font-semibold text-pp-text"
            data-testid="admin-contest-config-entries-badge"
          >
            {entriesCurrent} / {capValue == null ? "∞" : capValue} entries
          </div>
          <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
            <DialogTrigger asChild>
              <Button
                type="button"
                variant="secondary"
                className="rounded-full"
                data-testid="admin-contest-config-close-round-btn"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Close round + start new
              </Button>
            </DialogTrigger>
            <DialogContent
              className="rounded-3xl bg-pp-surface"
              data-testid="admin-contest-config-close-modal"
            >
              <DialogHeader>
                <DialogTitle className="font-heading text-lg text-pp-text">
                  Close this round + start a new one
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                This will freeze the current round's entries + winners, then
                open a fresh draft round you can configure.
              </p>
              <div className="mt-4 space-y-2">
                <Label htmlFor="next-round-name" className="text-sm font-semibold text-pp-text">
                  New round name
                </Label>
                <Input
                  id="next-round-name"
                  value={nextRoundName}
                  onChange={(e) => setNextRoundName(e.target.value)}
                  placeholder="Cutest Pluck 2026 Round 2"
                  className="h-11 rounded-full border-border bg-pp-surface px-5"
                  data-testid="admin-contest-config-next-round-name"
                />
              </div>
              <DialogFooter className="mt-4 flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setCloseOpen(false)}
                  className="rounded-full"
                  data-testid="admin-contest-config-close-modal-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleCloseRoundAndStartNew}
                  disabled={!nextRoundName.trim() || closingRound}
                  className="rounded-full bg-pp-purple text-white hover:bg-pp-purple-dark"
                  data-testid="admin-contest-config-close-modal-confirm"
                >
                  {closingRound ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Close + start
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {creditedCount > 0 && (
        <div
          className="flex items-start gap-3 rounded-2xl border border-border bg-pp-surface-2 p-4"
          data-testid="admin-contest-config-credited-warning"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 text-brand-orange" />
          <div className="text-sm text-pp-text">
            <p className="font-semibold">
              Prize credits already issued to {creditedCount}{" "}
              {creditedCount === 1 ? "entry" : "entries"}.
            </p>
            <p className="mt-1 text-muted-foreground">
              Changing the prize amount or winner count will NOT retroactively
              adjust existing credits. Only entries ranked AFTER the change
              will pick up the new values.
            </p>
          </div>
        </div>
      )}

      {postSaveWarnings.includes("non_retroactive_winner_count_change") && (
        <div
          className="rounded-2xl border border-border bg-pp-surface-2 p-4 text-sm text-pp-text"
          data-testid="admin-contest-config-winner-count-warning"
        >
          Winner count changed after prizes were issued. Existing credits stay
          put; the new value applies to future ranks only.
        </div>
      )}

      <div className="rounded-3xl border border-border bg-pp-surface p-6">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2 flex items-center justify-between rounded-2xl bg-pp-surface-2 p-4">
            <div>
              <Label className="text-sm font-semibold text-pp-text">
                Contest active
              </Label>
              <p className="text-xs text-muted-foreground">
                Accepts new entries and voting when on.
              </p>
            </div>
            <Switch
              checked={isActive}
              onCheckedChange={setIsActive}
              data-testid="admin-contest-config-is-active"
            />
          </div>

          {/* Item 1 (2026-07-24) — public display name.
              Distinct from the raw `name` field surfaced in the H1 above
              (which admins keep as the internal round identifier).
              This field controls what the public sees on the rules page
              H1 + share_contest OG title/description via the shared
              `getContestDisplayName` accessor. */}
          <div className="sm:col-span-2">
            <Label htmlFor="cfg-display-name" className="text-sm font-semibold text-pp-text">
              Contest display name (public)
            </Label>
            <Input
              id="cfg-display-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Cutest Pluck — Winter 2027"
              maxLength={DISPLAY_NAME_MAX_LEN}
              className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
              aria-invalid={displayNameWarning ? "true" : "false"}
              aria-describedby={
                displayNameWarning
                  ? "admin-contest-config-display-name-warning"
                  : "admin-contest-config-display-name-preview"
              }
              data-testid="admin-contest-config-display-name"
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Leave blank to use the default &ldquo;{CONTEST_DISPLAY_NAME_FALLBACK}&rdquo;.
            </p>
            {displayNameWarning ? (
              <div
                id="admin-contest-config-display-name-warning"
                role="alert"
                className="mt-2 flex items-start gap-2 rounded-xl bg-pp-surface-2 p-3 text-xs text-brand-orange"
                data-testid="admin-contest-config-display-name-warning"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  That looks like an internal round ID. Enter a friendly
                  public name (e.g. &ldquo;Cutest Pluck &mdash; Winter
                  2027&rdquo;) or leave blank to use the default.
                </span>
              </div>
            ) : (
              <p
                id="admin-contest-config-display-name-preview"
                className="mt-2 text-xs text-muted-foreground"
                data-testid="admin-contest-config-display-name-preview"
              >
                Public will see:{" "}
                <span
                  className="font-semibold text-pp-text"
                  data-testid="admin-contest-config-display-name-preview-value"
                >
                  {previewName}
                </span>
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="cfg-entry-open" className="text-sm font-semibold text-pp-text">
              Entry opens
            </Label>
            <Input
              id="cfg-entry-open"
              type="datetime-local"
              value={entryOpen}
              onChange={(e) => setEntryOpen(e.target.value)}
              className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
              data-testid="admin-contest-config-entry-open"
            />
          </div>
          <div>
            <Label htmlFor="cfg-entry-close" className="text-sm font-semibold text-pp-text">
              Entry closes
            </Label>
            <Input
              id="cfg-entry-close"
              type="datetime-local"
              value={entryClose}
              onChange={(e) => setEntryClose(e.target.value)}
              className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
              data-testid="admin-contest-config-entry-close"
            />
          </div>
          <div>
            <Label htmlFor="cfg-voting-close" className="text-sm font-semibold text-pp-text">
              Voting closes
            </Label>
            <Input
              id="cfg-voting-close"
              type="datetime-local"
              value={votingClose}
              onChange={(e) => setVotingClose(e.target.value)}
              className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
              data-testid="admin-contest-config-voting-close"
            />
          </div>
          <div>
            <Label htmlFor="cfg-prize" className="text-sm font-semibold text-pp-text">
              Prize amount (USD)
            </Label>
            <Input
              id="cfg-prize"
              type="number"
              step="0.01"
              min="0.01"
              max="500"
              value={prizeDollars}
              onChange={(e) => setPrizeDollars(e.target.value)}
              className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
              data-testid="admin-contest-config-prize"
            />
          </div>

          <div>
            <Label htmlFor="cfg-max-entries" className="text-sm font-semibold text-pp-text">
              Max entries (blank = unlimited)
            </Label>
            <Input
              id="cfg-max-entries"
              type="number"
              min="1"
              value={maxEntries}
              onChange={(e) => setMaxEntries(e.target.value)}
              className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
              placeholder="e.g. 50"
              data-testid="admin-contest-config-max-entries"
            />
          </div>
          <div>
            <Label htmlFor="cfg-winner-count" className="text-sm font-semibold text-pp-text">
              Winner count (1–10)
            </Label>
            <Input
              id="cfg-winner-count"
              type="number"
              min="1"
              max="10"
              value={winnerCount}
              onChange={(e) => setWinnerCount(e.target.value)}
              className="mt-2 h-12 rounded-full border-border bg-pp-surface px-5"
              data-testid="admin-contest-config-winner-count"
            />
          </div>
        </div>

        {inlineError && (
          <div
            className="mt-4 rounded-2xl bg-pp-surface-2 p-3 text-sm text-brand-orange"
            data-testid="admin-contest-config-inline-error"
          >
            {inlineError}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || displayNameWarning}
            className="rounded-full bg-pp-purple text-white hover:bg-pp-purple-dark"
            data-testid="admin-contest-config-save"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}
